import i18n from 'i18n'
import { MetricSeries as MetricSeriesType } from 'schemas/MetricSeriesSchema'
import MeasureType, { MeasureTypeStrings } from 'enums/MeasureType'
import ReturnStatus from 'enums/ReturnStatus'
import messageUtil from 'utils/messageUtil'
import { MessageConst } from 'enums'
import { formatByMeasureType, formatDeltaByMeasureType } from 'utils/measureTypeUtil'
import metricSeriesToMeasure from './metricSeriesToMeasure'

const getNotAvailableTooltipLabelByMeasureType = (measure: MeasureTypeStrings) => {
  const messageMap = {
    [MeasureType.NET_SENTIMENT]: 'notAvailableNetSentiment',
    [MeasureType.PASSION_INTENSITY]: 'notAvailablePassionIntensity',
    [MeasureType.POSITIVES]: 'notAvailablePositives',
    [MeasureType.NEGATIVES]: 'notAvailableNegatives'
  }
  return i18n.t(messageMap[measure]) || ''
}

enum Trend {
  up = 'up',
  down = 'down',
  none = 'none'
}

const measureToColorSetting = {
  [MeasureType.UNIQUE_AUTHORS]: 'metric.mainMetrics.authors',
  [MeasureType.MENTIONS]: 'metric.mainMetrics.mentions',
  [MeasureType.POSTS]: 'metric.mainMetrics.post',
  [MeasureType.IMPRESSIONS]: 'metric.mainMetrics.potentialImpressions',
  [MeasureType.NET_SENTIMENT]: 'metric.mainMetrics.netSentiment',
  [MeasureType.PASSION_INTENSITY]: 'metric.mainMetrics.passionIntensity',
  [MeasureType.POSITIVES]: 'semantic.positive',
  [MeasureType.NEGATIVES]: 'semantic.negative'
}

const valueFormatter = (measure: MeasureTypeStrings, value?: number) => {
  if (value === undefined) {
    return MessageConst.NOT_AVAILABLE_ABBR
  }
  return formatByMeasureType(value, measure, { compactFormatThreshold: 1000 })
}

const deltaValueFormatter = (measure: MeasureTypeStrings, value?: number, prevValue?: number) => {
  if (value === undefined || prevValue === undefined || prevValue === 0) {
    return MessageConst.EM_DASH
  }
  return formatDeltaByMeasureType(measure, value, prevValue)
}

const getTrend = (value?: number, prevValue?: number): Trend => {
  if (value === undefined || prevValue === undefined || prevValue === 0) {
    return Trend.none
  }
  if (value > prevValue) {
    return Trend.up
  }
  if (value < prevValue) {
    return Trend.down
  }
  return Trend.none
}

const convertToTileData = (dataset: MetricSeriesType[], prevDataset: MetricSeriesType[]) => {
  const measures = dataset.map((data) => metricSeriesToMeasure[data.seriesName!])
  const measureToValue = dataset.reduce((accumulator, data) => {
    const value =
      data.status === ReturnStatus.LANGUAGE_NOT_SUPPORTED ? undefined : (data.set![0] as number)
    return { ...accumulator, [metricSeriesToMeasure[data.seriesName!]]: value }
  }, {} as Record<string, number | undefined>)
  const prevMeasureToValue = prevDataset.reduce((accumulator, data) => {
    const value =
      data.status === ReturnStatus.LANGUAGE_NOT_SUPPORTED ? undefined : (data.set![0] as number)
    return { ...accumulator, [metricSeriesToMeasure[data.seriesName!]]: value }
  }, {} as Record<string, number | undefined>)

  return measures.map((measure) => {
    const isDisabled = measureToValue[measure] === undefined
    return {
      title: messageUtil.measureType(measure) || '',
      value: valueFormatter(measure, measureToValue[measure]),
      colorSetting: measureToColorSetting[measure],
      variation: {
        trend: getTrend(measureToValue[measure], prevMeasureToValue[measure]),
        value: deltaValueFormatter(measure, measureToValue[measure], prevMeasureToValue[measure])
      },
      isDisabled,
      infoTooltipContent: isDisabled
        ? getNotAvailableTooltipLabelByMeasureType(measure)
        : messageUtil.getTooltipLabelByMeasureType(measure)
    }
  })
}

export default convertToTileData
